// Training-comments feature API client.
//
// Mirrors frontend/src/features/posting-engagement/CommentThread.tsx's
// exported API helpers, but targets a training resource's comment thread
// instead of a post's:
//   POST /api/v1/training/{resource_id}/comments
//   PUT  /api/v1/training/{resource_id}/comments/{comment_id}
//
// Types mirror api/schemas/training_comments.py.

import { apiClient } from "@/lib/api-client";
import type { ReactionSummary } from "@/features/posting-engagement/reactions-api";

export interface TrainingCommentCreate {
  text: string;
  /** Optional GIF attachment (from the GIF picker) — an addition, not a replacement. */
  gif_url?: string | null;
  gif_title?: string | null;
}

/** Request shape for PUT /api/v1/training/{resource_id}/comments/{id}. */
export type TrainingCommentUpdate = TrainingCommentCreate;

export interface TrainingCommentItem {
  id: number;
  author_id: string | null;
  author_name?: string | null;
  author_initials?: string | null;
  author_division?: string | null;
  text: string;
  vote_count: number;
  created_at: string; // ISO 8601
  /** Set only when the comment was edited after creation. */
  updated_at?: string | null;
  gif_url?: string | null;
  gif_title?: string | null;
  reactions?: ReactionSummary[];
}

/**
 * POST /api/v1/training/{resource_id}/comments
 *
 * Creates a comment attributed to the authenticated session user.
 * Throws ApiError on 401 (unauthenticated) or 422 (invalid text).
 */
export async function createTrainingComment(
  resourceId: string,
  data: TrainingCommentCreate,
): Promise<TrainingCommentItem> {
  return apiClient<TrainingCommentItem>(
    `/api/v1/training/${encodeURIComponent(resourceId)}/comments`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    },
  );
}

/**
 * PUT /api/v1/training/{resource_id}/comments/{id}
 *
 * Edit an existing comment (author self-service, or admin moderation). The
 * server enforces that the caller is the comment's author OR an admin (403
 * otherwise). Returns the updated comment, including `updated_at`.
 */
export async function editTrainingComment(
  resourceId: string,
  commentId: number,
  data: TrainingCommentUpdate,
): Promise<TrainingCommentItem> {
  return apiClient<TrainingCommentItem>(
    `/api/v1/training/${encodeURIComponent(resourceId)}/comments/${commentId}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    },
  );
}

/**
 * DELETE /api/v1/training/{resource_id}/comments/{id}
 *
 * Delete a comment (author self-service, or admin moderation). The server
 * enforces that the caller is the comment's author OR an admin (403
 * otherwise). Deletion is immediate and permanent — there is no undo.
 */
export async function deleteTrainingComment(
  resourceId: string,
  commentId: number,
): Promise<void> {
  await apiClient<{ removed: boolean; comment_id: number }>(
    `/api/v1/training/${encodeURIComponent(resourceId)}/comments/${commentId}`,
    { method: "DELETE" },
  );
}
